/**
 * Allocate Receipt Dialog Component
 * Allows users to allocate receipts to outstanding invoices
 */

import { AlertCircle, CheckCircle2, Receipt, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency, formatDateDDMMYYYY } from '@/lib/utils';
import type { Transaction } from '@/types/transaction';

interface AllocationItem {
  invoiceId: number;
  amount: number;
}

interface AllocateReceiptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receipts: Transaction[];
  availableInvoices: Transaction[];
  onAllocate: (receiptId: number, allocations: AllocationItem[]) => void;
}

export function AllocateReceiptDialog({
  open,
  onOpenChange,
  receipts,
  availableInvoices,
  onAllocate,
}: AllocateReceiptDialogProps) {
  const [selectedReceiptId, setSelectedReceiptId] = useState<number | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<Set<number>>(new Set());
  const [allocationAmounts, setAllocationAmounts] = useState<Record<number, number>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isAllocating, setIsAllocating] = useState(false);

  // Filter receipts with outstanding balance
  const unallocatedReceipts = receipts.filter((r) => r.transactionType === 'receipt' && (r.outstandingAmount || 0) > 0);

  const selectedReceipt = unallocatedReceipts.find((r) => r.id === selectedReceiptId) || null;

  // Filter invoices by customer and search
  const filteredInvoices = useMemo(() => {
    if (!selectedReceipt) return [];

    let filtered = availableInvoices.filter(
      (inv) =>
        inv.transactionType === 'invoice' &&
        inv.customerId === selectedReceipt.customerId &&
        (inv.status === 'outstanding' || inv.status === 'overdue') &&
        (inv.outstandingAmount || 0) > 0,
    );

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (inv) => inv.referenceNumber.toLowerCase().includes(query) || inv.description.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [selectedReceipt, availableInvoices, searchQuery]);

  const handleToggleInvoice = (invoiceId: number) => {
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(invoiceId)) {
      newSelected.delete(invoiceId);
      const newAmounts = { ...allocationAmounts };
      delete newAmounts[invoiceId];
      setAllocationAmounts(newAmounts);
    } else {
      newSelected.add(invoiceId);
      // Auto-fill with outstanding amount or remaining receipt amount
      const invoice = filteredInvoices.find((inv) => inv.id === invoiceId);
      if (invoice) {
        const remainingReceiptAmount = getRemainingReceiptAmount();
        const suggestedAmount = Math.min(invoice.outstandingAmount || 0, remainingReceiptAmount);
        setAllocationAmounts({
          ...allocationAmounts,
          [invoiceId]: suggestedAmount,
        });
      }
    }
    setSelectedInvoices(newSelected);
  };

  const handleAmountChange = (invoiceId: number, value: string) => {
    const amount = parseFloat(value) || 0;
    setAllocationAmounts({
      ...allocationAmounts,
      [invoiceId]: amount,
    });
  };

  const getRemainingReceiptAmount = () => {
    if (!selectedReceipt) return 0;
    const totalAllocated = Object.values(allocationAmounts).reduce((sum, amt) => sum + amt, 0);
    return (selectedReceipt.outstandingAmount || 0) - totalAllocated;
  };

  const getTotalAllocated = () => {
    return Object.values(allocationAmounts).reduce((sum, amt) => sum + amt, 0);
  };

  const isValid = () => {
    if (!selectedReceipt || selectedInvoices.size === 0) return false;
    const total = getTotalAllocated();
    const remaining = getRemainingReceiptAmount();
    return total > 0 && remaining >= 0;
  };

  const handleAllocate = async () => {
    if (!selectedReceipt || !isValid()) return;

    setIsAllocating(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    const allocations: AllocationItem[] = Array.from(selectedInvoices).map((invoiceId) => ({
      invoiceId,
      amount: allocationAmounts[invoiceId] || 0,
    }));

    onAllocate(selectedReceipt.id, allocations);
    setIsAllocating(false);

    // Reset state
    setSelectedReceiptId(null);
    setSelectedInvoices(new Set());
    setAllocationAmounts({});
    setSearchQuery('');
    onOpenChange(false);
  };

  const handleAutoAllocate = () => {
    if (!selectedReceipt) return;

    let remainingAmount = selectedReceipt.outstandingAmount || 0;
    const newAllocations: Record<number, number> = {};
    const newSelected = new Set<number>();

    // Allocate to invoices in order (oldest first)
    const sortedInvoices = [...filteredInvoices].sort(
      (a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime(),
    );

    for (const invoice of sortedInvoices) {
      if (remainingAmount <= 0) break;

      const invoiceOutstanding = invoice.outstandingAmount || 0;
      const allocationAmount = Math.min(invoiceOutstanding, remainingAmount);

      newAllocations[invoice.id] = allocationAmount;
      newSelected.add(invoice.id);
      remainingAmount -= allocationAmount;
    }

    setAllocationAmounts(newAllocations);
    setSelectedInvoices(newSelected);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Allocate Receipt to Invoices
          </DialogTitle>
          <DialogDescription>Allocate receipt payments to outstanding invoices for the same customer</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Receipt Selection */}
          <div className="space-y-2">
            <Label htmlFor="receipt-select">Select Receipt</Label>
            <Select
              value={selectedReceiptId?.toString() ?? ''}
              onValueChange={(value) => setSelectedReceiptId(value ? parseInt(value, 10) : null)}
            >
              <SelectTrigger id="receipt-select">
                <SelectValue placeholder="Choose a receipt to allocate..." />
              </SelectTrigger>
              <SelectContent>
                {unallocatedReceipts.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No unallocated receipts in selection
                  </div>
                ) : (
                  unallocatedReceipts.map((receipt) => (
                    <SelectItem key={receipt.id} value={receipt.id.toString()}>
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-medium">{receipt.referenceNumber}</span>
                        <span className="text-muted-foreground">{receipt.customerName}</span>
                        <Badge variant="secondary">
                          {formatCurrency(receipt.outstandingAmount || 0, receipt.currency)}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedReceipt && (
            <>
              {/* Receipt Summary */}
              <Alert className="bg-blue-50 border-blue-200">
                <Receipt className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <strong>{selectedReceipt.referenceNumber}</strong> • {selectedReceipt.customerName}
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">
                        Available: {formatCurrency(getRemainingReceiptAmount(), selectedReceipt.currency)}
                      </div>
                      <div className="text-xs">
                        Total: {formatCurrency(selectedReceipt.outstandingAmount || 0, selectedReceipt.currency)}
                      </div>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>

              {/* Invoice Selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">
                    Select Invoices to Allocate ({selectedInvoices.size} selected)
                  </Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAutoAllocate}
                    disabled={filteredInvoices.length === 0}
                  >
                    Auto-Allocate
                  </Button>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search invoices..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Invoice List */}
                <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                  {filteredInvoices.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No outstanding invoices found for this customer</p>
                    </div>
                  ) : (
                    filteredInvoices.map((invoice) => (
                      <div key={invoice.id} className="flex items-start gap-3 p-3 hover:bg-muted/50 transition-colors">
                        <Checkbox
                          checked={selectedInvoices.has(invoice.id)}
                          onCheckedChange={() => handleToggleInvoice(invoice.id)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{invoice.referenceNumber}</span>
                            {invoice.status === 'overdue' && (
                              <Badge variant="destructive" className="text-xs">
                                Overdue
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                            <span>Date: {formatDateDDMMYYYY(invoice.transactionDate)}</span>
                            {invoice.dueDate && (
                              <>
                                <span>•</span>
                                <span>Due: {formatDateDDMMYYYY(invoice.dueDate)}</span>
                              </>
                            )}
                            <span>•</span>
                            <span className="font-medium text-foreground">
                              Outstanding: {formatCurrency(invoice.outstandingAmount || 0, invoice.currency)}
                            </span>
                          </div>
                          {selectedInvoices.has(invoice.id) && (
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`amount-${invoice.id}`} className="text-xs">
                                Allocate:
                              </Label>
                              <Input
                                id={`amount-${invoice.id}`}
                                type="number"
                                min="0"
                                max={Math.min(invoice.outstandingAmount || 0, selectedReceipt.outstandingAmount || 0)}
                                step="0.01"
                                value={allocationAmounts[invoice.id] || ''}
                                onChange={(e) => handleAmountChange(invoice.id, e.target.value)}
                                className="w-32 h-8"
                              />
                              <span className="text-xs text-muted-foreground">{selectedReceipt.currency}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Allocation Summary */}
              {selectedInvoices.size > 0 && (
                <Alert
                  className={
                    getRemainingReceiptAmount() >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                  }
                >
                  {getRemainingReceiptAmount() >= 0 ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  )}
                  <AlertDescription className={getRemainingReceiptAmount() >= 0 ? 'text-green-800' : 'text-red-800'}>
                    <div className="flex items-center justify-between">
                      <div>
                        Allocating to <strong>{selectedInvoices.size}</strong> invoice
                        {selectedInvoices.size !== 1 ? 's' : ''}
                      </div>
                      <div className="text-right">
                        <div>
                          Total Allocated:{' '}
                          <strong>{formatCurrency(getTotalAllocated(), selectedReceipt.currency)}</strong>
                        </div>
                        <div className="text-xs">
                          Remaining:{' '}
                          <strong>{formatCurrency(getRemainingReceiptAmount(), selectedReceipt.currency)}</strong>
                        </div>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isAllocating}>
            Cancel
          </Button>
          <Button onClick={handleAllocate} disabled={!isValid() || isAllocating} className="gap-2">
            {isAllocating ? (
              <>
                <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Allocating...
              </>
            ) : (
              <>
                <Receipt className="h-4 w-4" />
                Allocate Receipt
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
